import type { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faHorizontalRule } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Html, useCursor } from "@react-three/drei";
import { type Euler, useThree } from "@react-three/fiber";
import { cn } from "@tosize/ui";
import { type ComponentProps, Fragment, forwardRef, useMemo, useRef, useState } from "react";
import { BufferGeometry, type Mesh, Vector3 } from "three";
import { match } from "ts-pattern";
import { type CabinetState, useCabinetStore } from "../../../../context/StoreContext";
import { getDimensions } from "../../../../hooks/getDimensions";
import { useCanvasStore } from "../../../../hooks/useCanvasStore";
import { useDrag } from "../../context/DragContext";
import { useMaterialContext } from "../../context/MaterialContext";
import type { ApiData } from "../../data";
import {
	type CompartmentType,
	type Side,
	getCombinedHeight,
	getCombinedLength,
	getCombinedX,
	getCombinedY,
	getCompartmentsOnSide,
	getMaxSize,
	getMinSize,
	getNewXValue,
	getNewYValue,
} from "../../data/Compartment";
import SizePill from "../../utils/SizePill";

export function Draggers() {
	const { state } = useCabinetStore();
	const { fullScreen } = useCanvasStore();

	const verticals = useMemo(() => {
		const map: Map<string, { x: number; y: number; z: number; length: number }> = new Map();

		// biome-ignore lint/complexity/noForEach: Type not set correctly when using for loop
		state.compartments.forEach((c) => {
			const y = getCombinedY(c, state.compartments, state.thickness, "left");
			const length = getCombinedHeight(c, state.compartments, state.thickness, "left");
			const x = c.x;

			if (length > 0) {
				map.set(`${x}-${y}-${length}`, {
					x: x,
					y: y,
					z: c.z + c.depth,
					length: length,
				});
			}
		});

		return [...map.values()];
	}, [state.compartments, state.thickness]);

	const horizontals = useMemo(() => {
		const map: Map<string, { x: number; y: number; z: number; length: number }> = new Map();

		// biome-ignore lint/complexity/noForEach: Type not set correctly when using for loop
		state.compartments.forEach((c) => {
			const x = getCombinedX(c, state.compartments, state.thickness, "bottom");
			const length = getCombinedLength(c, state.compartments, state.thickness, "bottom");

			const y = c.y;

			if (length > 0 && y > state.thickness) {
				map.set(`${x}-${y}-${length}`, {
					x: x,
					y: y,
					z: c.z + c.depth,
					length: length,
				});
			}
		});

		return [...map.values()];
	}, [state.compartments, state.thickness]);

	if (fullScreen) return null;

	return (
		<Fragment>
			{verticals.map((h, i) => (
				<Dragger key={i} position={{ x: h.x, y: h.y, z: h.z }} length={h.length} vertical />
			))}

			{horizontals.map((h, i) => (
				<Dragger key={i} position={{ x: h.x, y: h.y, z: h.z }} length={h.length} vertical={false} />
			))}
		</Fragment>
	);
}

export const getBoundsForCompartment = (
	compartment: CompartmentType,
	side: Side,
	state: CabinetState,
	api?: ApiData,
) => {
	const min = getMinSize(compartment, side, state, api);
	const max = getMaxSize(compartment, side, state, api);

	return match(side)
		.with("top", "bottom", (side) => {
			const top = (max - compartment.height) / 1000;
			const bottom = (compartment.height - min) / 1000;

			return side === "top"
				? { top, bottom, left: 0, right: 0 }
				: { top: bottom, bottom: top, left: 0, right: 0 };
		})
		.with("left", "right", (side) => {
			const left = (max - compartment.width) / 1000;
			const right = (compartment.width - min) / 1000;

			return side === "left"
				? { top: 0, bottom: 0, left, right }
				: { top: 0, bottom: 0, left: right, right: left };
		})
		.exhaustive();
};

export function inverseSide(side: Side) {
	return match(side)
		.with("left", () => "right")
		.with("right", () => "left")
		.with("top", () => "bottom")
		.with("bottom", () => "top")
		.exhaustive() as Side;
}

export function calculateDifference(
	position: { x: number; y: number; z: number },
	mesh: Mesh,
	isVertical: boolean,
) {
	// calculate the difference of what the dragger has moved in millimeters
	const difference = match(isVertical)
		.with(true, () => (position.x - mesh.position.x * 1000) * -1)
		.with(false, () => (position.y - mesh.position.y * 1000) * -1)
		.exhaustive();

	return difference;
}

interface DraggerProps {
	vertical: boolean;
	position: { x: number; y: number; z: number };
	length: number;
}

function Dragger({ position, length, vertical }: DraggerProps) {
	const { state, api, updateCompartmentWidth, updateCompartmentHeight } = useCabinetStore();
	const depth = 0.4;

	const { viewport, invalidate } = useThree((selector) => ({
		viewport: selector.viewport,
		invalidate: selector.invalidate,
	}));

	// the reference to the placeholder mesh so we can update the position without
	// using state
	const ref = useRef<Mesh>(null);

	const [affected, setAffected] = useState<CompartmentType[]>([]);
	const [difference, setDifference] = useState<number>();

	const rotation = match(vertical)
		.with(true, () => [0, 0, Math.PI / 2])
		.with(false, () => [0, 0, 0])
		.exhaustive() as Euler;

	const dimensions = {
		x: position.x / 1000,
		y: position.y / 1000,
		z: position.z / 1000,
	};

	const side = vertical ? "right" : "top";

	const compartment = useMemo(() => {
		const compartments = [...state.compartments.values()];

		const compartment = match(vertical)
			.with(true, () =>
				compartments.find(
					(c) => c.x + c.width + state.thickness === position.x && c.y === position.y,
				),
			)
			.with(false, () =>
				compartments.find(
					(c) => c.y + c.height + state.thickness === position.y && c.x === position.x,
				),
			)
			.exhaustive();

		if (!compartment) {
			throw new Error("No compartment found for drag");
		}

		return compartment;
	}, [position, state, vertical]);

	const bounds = useMemo(() => {
		const compartments = [...getCompartmentsOnSide(compartment, side, state).values()];

		const inverseCompartments = compartments
			.flatMap((compartment) => {
				return [...getCompartmentsOnSide(compartment, inverseSide(side), state).values()];
			})
			.map((compartment) => getBoundsForCompartment(compartment, side, state, api));

		const bounds = compartments.map((compartment) =>
			getBoundsForCompartment(compartment, inverseSide(side), state, api),
		);

		bounds.push(getBoundsForCompartment(compartment, side, state, api), ...inverseCompartments);

		if (bounds.length === 0) {
			return { top: 0, bottom: 0, left: 0, right: 0 };
		}

		return bounds.reduce((carry, next) => {
			if (!carry) return next;

			return {
				top: Math.min(carry.top, next.top),
				bottom: Math.min(carry.bottom, next.bottom),
				left: Math.min(carry.left, next.left),
				right: Math.min(carry.right, next.right),
			};
		});
	}, [compartment, side, state, api]);

	const { bind, isDragging } = useDrag({
		onDrag: ({ offset: [x, y] }) => {
			// update the position of the placeholder mesh
			match(vertical)
				.with(true, () => {
					if (ref.current) ref.current.position.x = dimensions.x + x / viewport.factor;
				})
				.with(false, () => {
					if (ref.current) ref.current.position.y = dimensions.y - y / viewport.factor;
				})
				.exhaustive();

			if (ref.current) {
				// update the amount of the movement to use in the size indicators
				setDifference(calculateDifference(position, ref.current, vertical));
			}

			// request a new render to make sure the dragging moves smoothly
			invalidate();
		},
		onDragStart: () => {
			// find the compartments that will have their size changed when the drag
			// has been completed so we can show the updated dimensions while dragging

			setAffected([...getCompartmentsOnSide(compartment, side, state).values()]);
		},
		onDragEnd: () => {
			// update the size of the actual compartments
			match(vertical)
				.with(true, () => {
					if (ref.current) {
						const difference = getNewXValue(
							compartment,
							"right",
							calculateDifference(position, ref.current, vertical),
							state,
							api,
						);

						updateCompartmentWidth(compartment, "right", difference);
					}
				})
				.with(false, () => {
					if (ref.current) {
						const difference = getNewYValue(
							compartment,
							"top",
							calculateDifference(position, ref.current, vertical),
							state,
							api,
						);

						updateCompartmentHeight(compartment, "top", difference);
					}
				})
				.exhaustive();

			// when the drag has ended we want to stop showing the size indicators
			setDifference(undefined);
			setAffected([]);
		},
		config: {
			drag: {
				from: [dimensions.x, dimensions.y],
				bounds: {
					top: -bounds.top * viewport.factor,
					bottom: bounds.bottom * viewport.factor,
					left: -bounds.left * viewport.factor,
					right: bounds.right * viewport.factor,
				},
			},
		},
	});

	useCursor(isDragging, "grabbing");

	const y = match(vertical)
		.with(true, () => dimensions.y + length / 1000 / 2)
		.with(false, () => dimensions.y - state.thickness / 1000 / 2)
		.exhaustive();

	const x = match(vertical)
		.with(true, () => dimensions.x - state.thickness / 1000 / 2)
		.with(false, () => dimensions.x + length / 1000 / 2)
		.exhaustive();

	const z = dimensions.z - depth / 2;
	const thickness = state.thickness / 1000;

	return (
		<Fragment>
			<DragMesh
				ref={ref}
				position={[x, y, z]}
				rotation={rotation}
				dimensions={[length / 1000, state.thickness / 1000, depth]}
				isDragging={isDragging}
			/>

			{isDragging && (
				<Fragment>
					<BoundsLine
						start={
							vertical
								? [x + bounds.right + thickness, y - length / 1000 / 2, dimensions.z]
								: [x - length / 1000 / 2, y - bounds.bottom, dimensions.z]
						}
						end={
							vertical
								? [x + bounds.right + thickness, y + length / 1000 / 2, dimensions.z]
								: [x + length / 1000 / 2, y - bounds.bottom, dimensions.z]
						}
					/>

					<BoundsLine
						start={
							vertical
								? [x - bounds.left, y + length / 1000 / 2, dimensions.z]
								: [x + length / 1000 / 2, y + bounds.top + thickness, dimensions.z]
						}
						end={
							vertical
								? [x - bounds.left, y - length / 1000 / 2, dimensions.z]
								: [x - length / 1000 / 2, y + bounds.top + thickness, dimensions.z]
						}
					/>
				</Fragment>
			)}

			<Html center position={[x, y, dimensions.z]}>
				<DragIcon className="px-1" icon={faHorizontalRule} vertical={vertical} {...bind()} />
			</Html>

			{affected.map((compartment) => (
				<SizeDifferencePill
					side={side}
					compartment={compartment}
					difference={difference ?? 0}
					key={compartment.id}
				/>
			))}

			{isDragging && (
				<SizeDifferencePill
					side={side}
					compartment={compartment}
					difference={(difference ?? 0) * -1}
				/>
			)}
		</Fragment>
	);
}

interface BoundsLineProps {
	start: [number, number, number];
	end: [number, number, number];
}

export function BoundsLine(props: BoundsLineProps) {
	const geometry = new BufferGeometry().setFromPoints([
		new Vector3(...props.start),
		new Vector3(...props.end),
	]);

	return (
		<lineSegments onUpdate={(line) => line.computeLineDistances()} geometry={geometry}>
			<lineDashedMaterial
				color={0x000000}
				dashSize={0.02}
				gapSize={0.01}
				linewidth={2}
				fog={false}
			/>
		</lineSegments>
	);
}

interface DragIconProps extends ComponentProps<"div"> {
	icon: IconProp;
	vertical: boolean;
}

export function DragIcon({ icon, vertical, className, ...props }: DragIconProps) {
	const [hovered, set] = useState(false);

	useCursor(hovered, "grab");

	return (
		<div
			onPointerOver={() => set(true)}
			onPointerOut={() => set(false)}
			className={cn(
				className,
				"flex touch-none items-center justify-center rounded-full border border-shades-medium bg-shades-light",
				{
					"rotate-90": vertical,
				},
			)}
			{...props}
		>
			<FontAwesomeIcon
				fixedWidth
				size="sm"
				className="pointer-events-none text-secondary-dark"
				icon={icon}
			/>
		</div>
	);
}

interface DragMeshProps extends ComponentProps<"mesh"> {
	dimensions: [width?: number, height?: number, depth?: number];
	isDragging: boolean;
}

export const DragMesh = forwardRef<Mesh, DragMeshProps>((props, ref) => {
	const { dimensions, isDragging, ...rest } = props;
	const { defaultMaterial } = useMaterialContext();

	return (
		<mesh {...rest} visible={isDragging} ref={ref} material={defaultMaterial}>
			<boxGeometry args={dimensions} />
		</mesh>
	);
});

DragMesh.displayName = "DragMesh";

interface SizeDifferencePillProps {
	compartment: CompartmentType;
	side: Side;
	difference: number;
}

export function SizeDifferencePill({ compartment, side, difference }: SizeDifferencePillProps) {
	const size = match(side)
		.with("left", () => compartment.width + difference)
		.with("right", () => compartment.width - difference)
		.with("top", () => compartment.height - difference)
		.with("bottom", () => compartment.height + difference)
		.exhaustive();

	const dimensions = getDimensions(compartment);

	return (
		<SizePill
			size={size}
			position={{ x: dimensions.x, y: dimensions.y, z: dimensions.z + dimensions.width / 2 }}
		/>
	);
}
